import { Box, Icon, Table, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { formatBackendDateToDayMonthYear } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { Amount } from 'modules/project_management/components/Amount'
import BillableTag from 'modules/project_management/components/labelTags/BillableTag'
import ImportProjectTypeTag from 'modules/project_management/components/labelTags/ImportProjectTypeTag'
import StatusTag from 'modules/project_management/components/labelTags/StatusTag'
import { isValidProjectStatus } from 'modules/project_management/lib/projectStatus'
import { ProjectsToImport } from 'modules/project_management/lib/types'
import { ImportedProjectProps } from 'modules/project_management/lib/types'

export interface LegalEntity {
  legalName: string
  currency: string
}

export interface CellContentProps {
  project: ProjectsToImport
  projectProp: ImportedProjectProps
  legalEntities: LegalEntity[]
  defaultCurrency: string
}

const ErrorCell = ({ project, projectProp }: CellContentProps) => {
  const [open, toggle] = useToggle()

  const errorMessage = () => {
    if (projectProp === ImportedProjectProps.subproject_names) {
      return (
        project[projectProp].length +
        ' ' +
        i18n.t('project_management.imports.table.headers.subprojects')
      )
    }

    if (!project[projectProp]) {
      return '-'
    }

    if (
      projectProp === ImportedProjectProps.planned_cost ||
      projectProp === ImportedProjectProps.planned_income ||
      projectProp === ImportedProjectProps.project_fixed_cost
    ) {
      const value = project[projectProp]

      if (isNaN(value)) {
        return value
      }
      return value / 100
    }

    return project[projectProp]
  }

  return (
    <Table.Cell colSpan={2}>
      <Tooltip.Alt
        attachment="bottom center"
        open={open}
        toggle={toggle}
        label={project.errors[projectProp]}
      >
        <Box flexDirection="row" alignItems="center">
          <Text size="200" color="radicalRed1000">
            {errorMessage()}
          </Text>
          <Icon.Medium icon="cross-circle" color="radicalRed1000" />
        </Box>
      </Tooltip.Alt>
    </Table.Cell>
  )
}

const CellContent = ({
  project,
  projectProp,
  legalEntities,
  defaultCurrency,
}: CellContentProps) => {
  // Errored content
  if (project.errors[projectProp]) {
    return (
      <ErrorCell
        project={project}
        projectProp={projectProp}
        legalEntities={legalEntities}
        defaultCurrency={defaultCurrency}
      />
    )
  }
  // Date content
  if (
    projectProp === ImportedProjectProps.project_due_date ||
    projectProp === ImportedProjectProps.project_start_date
  ) {
    const date = project[projectProp] && project[projectProp]
    return <Table.Cell colSpan={2}>{date ? formatBackendDateToDayMonthYear(date) : '-'}</Table.Cell>
  }
  // Subproject content
  if (projectProp === ImportedProjectProps.subproject_names) {
    return (
      <Table.Cell colSpan={2}>
        {project[projectProp].length +
          ' ' +
          i18n.t('project_management.imports.table.headers.subprojects')}
      </Table.Cell>
    )
  }
  // Type content
  if (projectProp === ImportedProjectProps.type) {
    return (
      <Table.Cell colSpan={2}>
        <Box inline>
          <ImportProjectTypeTag importProjectType={project[projectProp]} />
        </Box>
      </Table.Cell>
    )
  }
  // Status content
  if (projectProp === ImportedProjectProps.project_status) {
    const status = project[projectProp]

    if (isValidProjectStatus(status)) {
      return (
        <Table.Cell colSpan={2}>
          <Box inline>
            <StatusTag projectStatus={status} />
          </Box>
        </Table.Cell>
      )
    }
  }
  // Billable content
  if (projectProp === ImportedProjectProps.project_billable) {
    return (
      <Table.Cell colSpan={2}>
        <BillableTag isBillable={project[projectProp]} />
      </Table.Cell>
    )
  }

  // amount content
  if (
    projectProp === ImportedProjectProps.planned_cost ||
    projectProp === ImportedProjectProps.planned_income ||
    projectProp === ImportedProjectProps.project_fixed_cost
  ) {
    const amount = project[projectProp]

    if (amount === null) {
      return <Table.Cell colSpan={2}>-</Table.Cell>
    }

    const currency =
      legalEntities.find((entity) => entity.legalName === project.project_legal_entity)?.currency ||
      defaultCurrency

    return (
      <Table.Cell colSpan={2}>
        <Amount amount={project[projectProp]} currency={currency} />
      </Table.Cell>
    )
  }

  // Default content
  if (project[projectProp]) {
    return <Table.Cell colSpan={2}>{project[projectProp]}</Table.Cell>
  }

  // Empty content
  return <Table.Cell colSpan={2}>-</Table.Cell>
}

export default CellContent
